/**
 * @file toast.service.ts
 * @description In-app, ephemeral notification queue. Rendered by
 * `<kirigami-toaster>` (mounted once in `app.html`). Each toast has a
 * `kind` (success/error/info), an optional body and click handler, and
 * an auto-dismiss timer (set `durationMs: 0` to keep the toast until
 * the user dismisses it manually).
 *
 * Cap is 3 visible at once — the 4th evicts the oldest. Intentionally
 * minimal: priorities, actions arrays, dismiss-all-of-kind etc. are
 * out of scope until a second consumer needs them.
 */
import { Injectable, computed, signal, type Signal } from '@angular/core';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  /** UUID-ish opaque id used by `*ngFor` track and `dismiss()`. */
  id: string;
  title: string;
  body?: string;
  kind: ToastKind;
  /** Auto-dismiss timeout in ms. `0` disables. */
  durationMs: number;
  /** Optional click handler — invoked when the toast body is clicked. */
  onClick?: () => void;
}

const MAX_VISIBLE = 3;
const DEFAULT_DURATION_MS = 6000;

@Injectable({ providedIn: 'root' })
export class ToastService {
  readonly #toasts = signal<readonly Toast[]>([]);
  readonly #timers = new Map<string, ReturnType<typeof setTimeout>>();

  readonly toasts: Signal<readonly Toast[]> = computed(() => this.#toasts());

  success(title: string, opts?: Partial<Omit<Toast, 'id' | 'kind' | 'title'>>): string {
    return this.show({ ...opts, kind: 'success', title });
  }

  error(title: string, opts?: Partial<Omit<Toast, 'id' | 'kind' | 'title'>>): string {
    return this.show({ ...opts, kind: 'error', title });
  }

  info(title: string, opts?: Partial<Omit<Toast, 'id' | 'kind' | 'title'>>): string {
    return this.show({ ...opts, kind: 'info', title });
  }

  show(toast: Omit<Toast, 'id' | 'durationMs'> & { durationMs?: number }): string {
    const id = this.#newId();
    const full: Toast = {
      id,
      title: toast.title,
      body: toast.body,
      kind: toast.kind,
      durationMs: toast.durationMs ?? DEFAULT_DURATION_MS,
      onClick: toast.onClick,
    };

    this.#toasts.update((list) => {
      const next = [...list, full];
      while (next.length > MAX_VISIBLE) {
        const evicted = next.shift();
        if (evicted) this.#cancelTimer(evicted.id);
      }
      return next;
    });

    if (full.durationMs > 0) {
      const handle = setTimeout(() => this.dismiss(id), full.durationMs);
      this.#timers.set(id, handle);
    }
    return id;
  }

  dismiss(id: string): void {
    this.#cancelTimer(id);
    this.#toasts.update((list) => list.filter((t) => t.id !== id));
  }

  clear(): void {
    for (const handle of this.#timers.values()) clearTimeout(handle);
    this.#timers.clear();
    this.#toasts.set([]);
  }

  #cancelTimer(id: string): void {
    const handle = this.#timers.get(id);
    if (handle !== undefined) {
      clearTimeout(handle);
      this.#timers.delete(id);
    }
  }

  #newId(): string {
    // Crypto-strong when available, falls back to Math.random for SSR/legacy.
    const crypto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (crypto?.randomUUID) return crypto.randomUUID();
    return `toast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }
}
